import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getAllProviderDetailsForClinic } from "../../../redux/slice/providerSlice";
import {
  Toast,
  closeModal,
  controlToastState,
  openModal,
} from "risa-oasis-ui_v2";
import CustomTable from "../../../components/custom-table/custom-table";
import AddMembersModal, { MemberModel } from "./add-members/add-members";
import { inviteNewMember } from "../../../api/firebase/firestoreService";
import InviteIcon from "../../../svg/invite-icon";
import { fetchInvitedMembers } from "../../../redux/slice/member-slice";
import { capitalizeWordsSeperatedByUnderScore } from "../../../utils/stringModifications";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import {
  MemberTableHeaderColumns,
  generateUIDataForMemberTable,
} from "./memberData";
import { LoaderMessage } from "../../../components/loaderMessage/loaderMessage";

const Members = () => {
  const { data: singleProviderDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const { data: allProviderDetails } = useSelector(
    (state: RootState) => state.allProviders,
  );

  const { data: memberData } = useSelector((state: RootState) => state.member);

  const dispatch = useDispatch<AppDispatch>();
  const [invitedMemberData, setInvitedMemberData] = useState<MemberModel[]>([]);
  const [signedUpMemberData, setSignedUpMemberData] = useState<MemberModel[]>(
    [],
  );
  const [allMemberData, setAllMemberData] = useState<any[]>([]);

  const openInviteMemberModal = () => {
    openModal("add-member-id");
  };

  const closeInviteMemberModal = () => {
    closeModal("add-member-id");
  };

  useEffect(() => {
    if (singleProviderDetails && singleProviderDetails?.IsAdmin === true) {
      dispatch(
        getAllProviderDetailsForClinic(singleProviderDetails.FacilityId || ""),
      );
      dispatch(fetchInvitedMembers());
    }
  }, [singleProviderDetails, dispatch]);

  // get all signed up members
  useEffect(() => {
    if (allProviderDetails) {
      const signedUpMembers: MemberModel[] = [];
      allProviderDetails.forEach((provider) => {
        if (
          provider &&
          provider?.FacilityId === singleProviderDetails?.FacilityId &&
          provider.Status === "signed_up"
        ) {
          const member: MemberModel = {
            email: provider.EmailAddresses ? provider.EmailAddresses[0] : "",
            name: generateFullName(
              provider.FirstName || "--",
              "",
              provider?.LastName || "",
            ),
            roleId: provider.IsAdmin ? "Admin" : "Nurse",
            status: "",
            facilityId: provider.FacilityId || "",
            isAdmin: provider.IsAdmin || false,
            id: provider.DocID || "",
          };
          signedUpMembers.push(member);
        }
      });

      setSignedUpMemberData(signedUpMembers);
    }
  }, [allProviderDetails, singleProviderDetails]);

  // get all invited members
  useEffect(() => {
    if (memberData) {
      const invitedMembers: MemberModel[] = [];
      memberData.forEach((member) => {
        if (
          member.facilityId === singleProviderDetails?.FacilityId &&
          (member.status === "invited" || member.status === "reinvited")
        ) {
          const capitalizedRole = capitalizeWordsSeperatedByUnderScore(
            member.roleId,
          );

          const updatedMember = {
            ...member,
            roleId: capitalizedRole,
            status: "Pending",
          };
          invitedMembers.push(updatedMember);
        }
      });
      setInvitedMemberData(invitedMembers);
    }
  }, [memberData, singleProviderDetails]);

  // combine all members
  useEffect(() => {
    if (memberData && allProviderDetails && singleProviderDetails) {
      setAllMemberData(
        generateUIDataForMemberTable([
          ...invitedMemberData,
          ...signedUpMemberData,
        ]),
      );
    }
  }, [
    memberData,
    allProviderDetails,
    singleProviderDetails,
    invitedMemberData,
    signedUpMemberData,
  ]);

  return (
    <>
      <div className="member-container">
        <h1>Members</h1>
        <button className="add-members-btn" onClick={openInviteMemberModal}>
          <InviteIcon />
          Invite Members
        </button>
        {invitedMemberData.length > 0 ? (
          <CustomTable
            tableHeaders={MemberTableHeaderColumns}
            tableData={allMemberData}
            count={allMemberData.length}
            itemsPerPage={12}
            pagesPerView={5}
            tableName="members"
          />
        ) : (
          <LoaderMessage message={"Fetching members data..."} />
        )}
        <div>
          {singleProviderDetails && singleProviderDetails?.FacilityId && (
            <AddMembersModal
              onSave={async function (memberModel: MemberModel): Promise<void> {
                closeInviteMemberModal();
                try {
                  await inviteNewMember(memberModel);
                  controlToastState("member_invite_success");
                } catch (error: any) {
                  controlToastState("member_invite_failed");
                }
              }}
              facilityId={singleProviderDetails.FacilityId}
            />
          )}
        </div>
        <Toast
          type={"success"}
          header={"Member reinvited !"}
          id={"member_reinvite_success"}
        />
        <Toast
          type={"error"}
          header={"Error reinviting member !"}
          id={"member_reinvite_failed"}
        />
        <Toast
          type={"success"}
          header={"New member added & invite link sent"}
          id={"member_invite_success"}
        />
        <Toast
          type={"error"}
          header={"Error inviting new member"}
          id={"member_invite_failed"}
        />
        <Toast
          type={"success"}
          header={"Member removed successfully"}
          id={"member_removed_success"}
        />
        <Toast
          type={"error"}
          header={"Error removing member"}
          id={"member_removed_failed"}
        />
      </div>
    </>
  );
};

export default Members;
